import ClassTeachingDetails from "@/components/ClassTeachingDetails";
/**
 * @fileoverview Class detail page. Mirrors the mobile ClassDetailScreen —
 * a kind/name hero, a stat strip, primary actions (Record, consent-gated),
 * a manageable roster with an undo toast on removal, a sessions (class
 * lessons) list, and class deletion via a Radix AlertDialog.
 *
 * Record gating mirrors the server-side check on
 * POST /classes/:id/lessons/start-upload: every member must have
 * consent_status === "granted".
 */

import { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, Mic, Plus, Upload, X, Trash2, ChevronRight, Lock, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import AddStudentsDialog from "@/components/AddStudentsDialog";
import { getClassKind } from "@/lib/classKinds";
import { Tag } from "@/components/studio/primitives";
import api, { getErrorMessage } from "@/lib/api";
import ExportMenu, { classExportItems } from "@/components/studio/ExportMenu";
import { formatLessonDuration } from "@/lib/duration";
import { recordedAt, sortChronologically } from "@/lib/lessonHistory";
import { toast } from "sonner";
import { WeeklySlotChip } from "@/components/WeeklySlotChip";
import ProgramChip from "@/components/ProgramChip";
import { Sidebar, MobileNav } from "@/components/Sidebar";
import { AuthContext } from "@/App";

const ClassDetail = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const [cls, setCls] = useState(null);
  const [students, setStudents] = useState([]);
  // The class's OWN roster, which a class taught with a colleague keeps in
  // their partition rather than in your student list.
  const [classMembers, setClassMembers] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchAll = useCallback(async (signal) => {
    try {
      const [classRes, studentsRes, lessonsRes, membersRes] = await Promise.all([
        api.get(`/classes/${classId}`, signal ? { signal } : {}),
        api.get("/students", signal ? { signal } : {}),
        api.get(`/classes/${classId}/lessons`, signal ? { signal } : {}).catch(() => ({ data: [] })),
        // The class's own roster, which is not always in yours: a class taught
        // with a colleague keeps its students under whoever the record is
        // filed under. Failing quietly falls the page back to your own list,
        // which is the right answer for a class of your own.
        api.get(`/classes/${classId}/members`, signal ? { signal } : {}).catch(() => ({ data: { members: [] } })),
      ]);
      setCls(classRes.data);
      setStudents(studentsRes.data || []);
      setLessons(sortChronologically(lessonsRes.data, recordedAt));
      setClassMembers(membersRes.data?.members || []);
    } catch (_error) {
      if (!signal?.aborted) {
        toast.error("Failed to load class");
        navigate("/students");
      }
    } finally {
      setLoading(false);
    }
  }, [classId, navigate]);

  useEffect(() => {
    const controller = new AbortController();
    fetchAll(controller.signal);
    return () => controller.abort();
  }, [fetchAll]);

  /* The class's roster. Your own students are the richer record — the row
     links through to a profile — so they win where you have them; a class you
     teach with a colleague falls back to the names the class itself supplies,
     which is the only place they exist for you. */
  const memberStudents = useMemo(() => {
    if (!cls) return [];
    return (cls.member_ids || [])
      .map((id) => students.find((s) => s.student_id === id) || classMembers.find((m) => m.student_id === id))
      .filter(Boolean);
  }, [cls, students, classMembers]);

  /* Whose record it is. Renaming it, deleting it and changing its roster stay
     with the instructor it is filed under, because the students in it are
     theirs; everyone who teaches it can record it and write it up. */
  const mine = !cls?.user_id || !user?.user_id || cls.user_id === user.user_id;

  const kind = getClassKind(cls?.kind);

  const handleAddMembers = async (ids) => {
    setPickerOpen(false);
    if (ids.length === 0) return;
    try {
      const { data } = await api.post(`/classes/${classId}/members`, { student_ids: ids });
      setCls(data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not add students"));
    }
  };

  const handleRemove = async (student) => {
    // Optimistic remove with an undo toast; commit happens immediately so a
    // lost page won't leave a stale member. Undo re-adds via the members API.
    const prevCls = cls;
    setCls({ ...cls, member_ids: (cls.member_ids || []).filter((id) => id !== student.student_id) });
    try {
      await api.delete(`/classes/${classId}/members/${student.student_id}`);
      toast.success(`Removed ${`${student.first_name || ""} ${student.last_name || ""}`.trim()}`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data } = await api.post(`/classes/${classId}/members`, {
                student_ids: [student.student_id],
              });
              setCls(data);
            } catch (err) {
              toast.error(getErrorMessage(err, "Could not undo"));
            }
          },
        },
      });
    } catch (error) {
      setCls(prevCls);
      toast.error(getErrorMessage(error, "Could not remove student"));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/classes/${classId}`);
      toast.success("Class deleted");
      navigate("/students");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not delete class"));
    }
  };

  const pendingConsent = memberStudents.filter((s) => s.consent_status !== "granted");
  const canRecord = memberStudents.length > 0 && pendingConsent.length === 0;

  const handleRecord = () => {
    if (memberStudents.length === 0) {
      toast.error("Add at least one student to record for this class.");
      return;
    }
    if (pendingConsent.length > 0) {
      const names = pendingConsent
        .map((s) => `${s.first_name || ""} ${s.last_name || ""}`.trim())
        .join(", ");
      toast.error(`Recording is locked until every member has accepted consent. Awaiting: ${names}`);
      return;
    }
    navigate(`/hans/class/${classId}`);
  };

  if (loading || !cls) {
    return (
      <div className="min-h-screen bg-[var(--studio-base)]">
        <Sidebar active="students" />
        <MobileNav active="students" />
        <main className="p-4 md:p-8 page-with-nav">
          <div className="space-y-4">
            <div className="h-8 w-48 rounded skeleton" />
            <div className="h-40 rounded-2xl skeleton" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--studio-base)]">
      <Sidebar active="students" />
      <MobileNav active="students" />

      <main className="p-4 md:p-8 page-with-nav" data-testid="class-detail">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/students")}
            className="text-[var(--text-tertiary)] active:text-[var(--text-primary)] -ml-2 touch-target"
            data-testid="class-detail-back"
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> Back
          </Button>
          {/* Deleting the class, and the roster edits below, belong to the
              instructor the record is filed under — its students are theirs.
              A colleague who teaches it records it and writes it up; they do
              not restructure it. */}
          {mine && <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="text-[var(--text-tertiary)] active:text-[var(--state-danger)] touch-target" aria-label="Delete class" data-testid="class-delete-button">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[var(--studio-overlay)] border-[var(--studio-divider-strong)] text-[var(--text-primary)] mx-4 max-w-[calc(100%-2rem)]">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete class?</AlertDialogTitle>
                <AlertDialogDescription className="text-[var(--text-secondary)]">
                  "{cls.name}" will be removed. Member students and their lessons stay in your roster.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-[var(--studio-interactive)] border-[var(--studio-divider-strong)] text-[var(--text-primary)] touch-target">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-[var(--state-danger)] text-[var(--text-on-state)] touch-target" data-testid="class-delete-confirm">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>}
        </div>

        <div className="max-w-3xl">
          {/* Header tile — the house card, headed the way the program class
              page heads itself: the name, then the kind as a tag. No kind
              colour: a kind is a label. */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-[20px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-5 md:p-6 mb-4"
            data-testid="class-hero"
          >
            <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">
              {cls.name || "Untitled class"}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Tag data-testid="class-kind-tag">{kind.label}</Tag>
            </div>
            {/* Where it meets — inside the tile, as the phone shows it. */}
            <ClassTeachingDetails cls={cls} />
            <div className="flex items-center gap-1.5 mt-3 font-sans text-[14px] text-[var(--text-tertiary)]">
              <Users className="w-3.5 h-3.5" />
              {memberStudents.length} {memberStudents.length === 1 ? "student" : "students"}
            </div>
          </motion.div>

          {/* Weekly slot pointer (Spec 04) — management lives on /schedule */}
          <div className="mb-4">
            <WeeklySlotChip classId={classId} />
          </div>

          {/* Actions */}
          <div className="mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={handleRecord}
                disabled={!canRecord}
                className="bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium rounded-full px-6 py-2 active:scale-95 transition-transform shadow-[0_0_30px_var(--accent-yellow-glow)] touch-target disabled:opacity-50"
                data-testid="class-record-button"
              >
                <Mic className="w-4 h-4 mr-2" /> Record
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate(`/lesson/new?class=${classId}`)}
                className="rounded-full px-5 py-2 text-[var(--text-primary)] touch-target"
                data-testid="class-add-lesson-notes-button"
              >
                <Pencil className="w-4 h-4 mr-2" /> Add lesson notes
              </Button>
            </div>
            {memberStudents.length > 0 && pendingConsent.length > 0 && (
              <div className="mt-3 p-3 rounded-xl bg-[var(--state-warning-dim)] border border-[var(--state-warning-border)] text-sm" data-testid="class-consent-banner">
                <p className="text-[var(--state-warning)] flex items-start gap-2">
                  <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    Recording is locked until every member has accepted consent. Awaiting:{" "}
                    {pendingConsent.map((s) => `${s.first_name || ""} ${s.last_name || ""}`.trim()).join(", ")}
                  </span>
                </p>
                <p className="mt-2 text-[var(--state-warning)]">
                  You can still{" "}
                  <button
                    onClick={() => navigate(`/lesson/new?class=${classId}`)}
                    className="underline text-[var(--accent-yellow-text)]"
                    data-testid="class-banner-add-lesson-notes"
                  >
                    add lesson notes
                  </button>{" "}
                  without recording.
                </p>
              </div>
            )}
          </div>

          {/* Roster */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Roster · {memberStudents.length}</h2>
            <div className="flex items-center gap-4">
              {memberStudents.length > 0 && (
                <ExportMenu
                  client={api}
                  errorMessage={getErrorMessage}
                  testId="class-export"
                  items={classExportItems(`/classes/${classId}`, cls.name)}
                />
              )}
              {/* Import sits beside Add, not instead of it: picking three names
                  off your own roster and taking a sixty-name list off an
                  academy are different jobs, and folding one into the other
                  makes the common case slower. */}
              <button
                onClick={() => navigate(`/students/import?class=${classId}`)}
                disabled={!mine || cls.enrollment_mode === "org_managed"}
                className="text-[var(--text-tertiary)] text-sm flex items-center gap-1 hover:text-[var(--text-primary)]"
                data-testid="class-import-students"
              >
                <Upload className="w-4 h-4" /> Import list
              </button>
              <button
                onClick={() => setPickerOpen(true)}
                disabled={!mine || cls.enrollment_mode === "org_managed"}
                className="text-[var(--accent-yellow-text)] text-sm flex items-center gap-1"
                data-testid="class-add-students"
              >
                <Plus className="w-4 h-4" /> Add students
              </button>
            </div>
          </div>

          {memberStudents.length === 0 ? (
            <button
              onClick={() => setPickerOpen(true)}
              disabled={!mine || cls.enrollment_mode === "org_managed"}
              className="w-full border border-dashed border-[var(--studio-divider-strong)] rounded-2xl p-8 text-center text-[var(--text-tertiary)] hover:bg-[var(--studio-overlay)] transition-colors mb-6"
              data-testid="class-empty-roster"
            >
              <Users className="w-6 h-6 text-[var(--text-quaternary)] mx-auto mb-2" />
              <p className="text-sm">No students yet. Add some.</p>
            </button>
          ) : (
            <div className="space-y-2 mb-2">
              {memberStudents.map((s) => (
                <div
                  key={s.student_id}
                  className="flex items-center gap-3 p-3 bg-[var(--studio-raised)] rounded-xl"
                  data-testid={`class-member-${s.student_id}`}
                >
                  <div className="w-10 h-10 bg-[var(--studio-interactive)] rounded-full flex items-center justify-center text-[var(--accent-yellow-text)] font-medium shrink-0">
                    {(s.first_name || "?").charAt(0).toUpperCase()}
                  </div>
                  <button
                    onClick={() => mine && navigate(`/students/${s.student_id}`)}
                    disabled={!mine}
                    className="flex-1 min-w-0 text-left"
                  >
                    <p className="text-[var(--text-primary)] text-sm font-medium truncate">{`${s.first_name || ""} ${s.last_name || ""}`.trim()}</p>
                    {s.instruments?.length > 0 && (
                      <p className="text-[var(--text-tertiary)] text-xs truncate">{s.instruments.join(" · ")}</p>
                    )}
                  </button>
                  <button
                    onClick={() => handleRemove(s)}
                    disabled={!mine || cls.enrollment_mode === "org_managed"}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-[var(--studio-divider-strong)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                    aria-label={`Remove ${s.first_name}`}
                    data-testid={`class-remove-${s.student_id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="text-[var(--text-quaternary)] text-xs mb-6">
            {cls.enrollment_mode === "org_managed" ? "Your organization manages this roster. Open a scheduled meeting to mark attendance and add lesson notes." : "Removing a student keeps their lesson history. They simply leave this class."}
          </p>

          {/* Sessions */}
          <h2 className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-3">Sessions · {lessons.length}</h2>
          {lessons.length === 0 ? (
            <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl p-8 text-center" data-testid="class-no-sessions">
              <Mic className="w-8 h-8 text-[var(--text-quaternary)] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[var(--text-tertiary)] text-sm">No lessons yet. Tap Record to capture your first class session.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {lessons.map((les) => (
                <button
                  key={les.lesson_id}
                  onClick={() => navigate(`/lesson/${les.lesson_id}?kind=class`)}
                  className="w-full flex items-center justify-between p-4 bg-[var(--studio-raised)] active:bg-[var(--studio-overlay)] rounded-xl transition-all touch-target"
                  data-testid={`class-session-${les.lesson_id}`}
                >
                  <div className="text-left min-w-0">
                    <p className="text-[var(--text-primary)] text-sm font-medium truncate">{les.title}</p>
                    <ProgramChip lesson={les} programTitle={les.program_title} orgName={les.org_name} accent={les.org_accent} />
                    <p className="text-[var(--text-tertiary)] text-xs">
                      {formatLessonDuration(les.duration_seconds)}
                      {les.processing_status && les.processing_status !== "complete" ? ` · ${les.processing_status}` : ""}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[var(--text-quaternary)] shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </main>

      <AddStudentsDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        existingIds={cls.member_ids || []}
        onConfirm={handleAddMembers}
      />
    </div>
  );
};

export default ClassDetail;
